import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject, OnInit, ViewRef } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { BasketViewComponentDef } from '@df/basket/view/basket-view.component.def';
import { DfDomModule } from '@df/dom/df-dom.module';
import { NGhostProducts } from '@df/module-ghost-products/ghost-products.namespace';
import { GhostProductsService } from '@df/module-ghost-products/ghost-products.service';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { IfNotStandaloneDirective } from '@df/module-pwa/utils/if-not-standalone.directive';
import { NgInject } from '@df/ng/ng-inject';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { LastCategoryPathPipe } from '@df/ui/common/last-category-path.pipe';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import difference from 'lodash-es/difference';
import sum from 'lodash-es/sum';
import sumBy from 'lodash-es/sumBy';
import { LocalProduct } from '../catalog/model/product/product.decorator';
import { ICmsOutletBlockComponent } from '../catalog/outlet-block/icms-outlet-block.component';
import { FlushBasketButtonComponent } from '../catalog/ui/basket/flush-basket-button/flush-basket-button';
import { addOnsGhostProdctRulesFilter } from '../catalog/ui/page/component/add-ons/add-ons-ghost-products-filter';
import { GiftcardsFormComponent } from '../module/giftcards/giftcards-form.component';
import { MentionMeWrapperComponent } from '../module/mention-me/mention-me-wrapper.component';
import { MentionMePlace, TMentionMeImplementation } from '@df/module-mention-me/mention-me.interfaces';
import { PaypalExpressButtonComponent } from '../module/paypal-express/paypal-express-button.component';
import { PaypalPayInThreeMessageComponent } from '../module/paypal-pay-in-three/paypal-pay-in-three-message.component';
import { StripeExpressPaymentRequestButtonComponent } from '../module/stripe/stripe-express-payment-request-button.component';
import { IfStoreDirective } from '../multisite/if-store.directive';
import { IfNotTradeStoreDirective } from '../multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from '../multisite/trade/if-trade-store.directive';
import { BottomPanelComponent } from '../ui/widget/bottom-panel/bottom-panel.component';
import { AntavoPreviewPointsBannerComponent } from './antavo-preview-points-banner/antavo-preview-points-banner.component';
import { BasketItemComponent } from './basket-item.component';
import { CouponFormComponent } from './coupon-form.component';
import { EcologiBannerBlockComponent } from './ecologi-banner/ecologi-banner-block.component';
import { PaymentMethodsLogosBlockComponent } from './payments-methods-block/payments-methods-logos-block.component';
import { RewardBannerComponent } from './reward-banner/reward-banner.component';
import { SurchargeInfoBlockComponent } from './surcharge-info/surcharge-info-block.component';
import { BasketViewTotalsComponent } from './totals/basket-view-totals.component';
import { LocalBasketItem } from '../customer/basket/basket-item.decorator';
import { LocalBasket } from './local-basket';

interface SplitedBasketElement {
    name: string;
    items: LocalBasketItem[];
    subtotal: number;
    qty: number;
}
@Component({
    selector: 'basket-view',
    templateUrl: './basket-view.html',
    styleUrls: ['./basket-view.scss'],
    standalone: true,
    imports: [
        AntavoPreviewPointsBannerComponent,
        BasketItemComponent,
        forwardRef(() => BasketViewTotalsComponent),
        BottomPanelComponent,
        CouponFormComponent,
        DfUiModule,
        EcologiBannerBlockComponent,
        DfDomModule,
        FlushBasketButtonComponent,
        GiftcardsFormComponent,
        ICmsOutletBlockComponent,
        IfNotTradeStoreDirective,
        IfStoreDirective,
        IfTradeStoreDirective,
        LastCategoryPathPipe,
        MentionMeWrapperComponent,
        NgClass,
        OrderByPipe,
        PaymentMethodsLogosBlockComponent,
        PaypalExpressButtonComponent,
        PaypalPayInThreeMessageComponent,
        PriceComponent,
        RewardBannerComponent,
        RouterLink,
        SizeClassDirective,
        StripeExpressPaymentRequestButtonComponent,
        SurchargeInfoBlockComponent,
        TranslationModule,
        IfNotStandaloneDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketViewComponent extends BasketViewComponentDef<LocalBasket> implements OnInit {
    addonsAvailable: boolean | null = null;
    protected ecologiTreeNumber: number = 0;

    protected router = inject(Router);
    protected ghostProductsService = inject(GhostProductsService);
    splitedBasket: SplitedBasketElement[] = [
        { name: 'In-Stock', items: [], subtotal: 0, qty: 0 },
        { name: 'Pre-Order', items: [], subtotal: 0, qty: 0 },
        { name: 'Backorder', items: [], subtotal: 0, qty: 0 }
    ];

    override ngOnInit(): void {
        this.subscriptions.add(
            this.basket.subscribe(() => {
                this.updateBasketSplit();
                this.updateEcologiNumber();
                this.updateAddons();
            })
        );
        this.updateBasketSplit();
        this.updateAddons();
        this.updateEcologiNumber();
        super.ngOnInit();
    }

    protected readonly MentionMePlace = MentionMePlace;

    goBack(): void {
        if (this.app.meta.previousState) {
            window.history.back();
            return;
        }
        this.router.navigateByUrl('/');
    }

    updateBasketSplit() {
        this.splitedBasket[2].items = this.basket.items.filter(
            item => item.isBackorderOnly() && !(item.product as LocalProduct).isPreOrder
        ) as LocalBasketItem[];
        this.splitedBasket[1].items = this.basket.items.filter(item => (item.product as LocalProduct).isPreOrder) as LocalBasketItem[];
        this.splitedBasket[0].items = difference(this.basket.items, [
            ...this.splitedBasket[1].items,
            ...this.splitedBasket[2].items
        ]) as LocalBasketItem[];
        this.splitedBasket.forEach(baskteSplited => {
            baskteSplited.qty = sumBy(baskteSplited.items, 'qty');
            baskteSplited.subtotal = sumBy(baskteSplited.items, 'data.rowPrice');
        });
    }

    trackByGiftcard(index: number, giftcard: Giftcards.IAddedGiftcardData) {
        return giftcard.code;
    }
    detectChanges(): void {
        if (!(<ViewRef>this._ref).destroyed) {
            this._ref.detectChanges();
        }
    }

    async updateAddons() {
        try {
            const result: NGhostProducts.IOfferPrepared[] = await this.ghostProductsService.load();
            this.addonsAvailable = !!result.find(addOnsGhostProdctRulesFilter)?.products?.length;
        } catch (err) {
            this.addonsAvailable = false;
        }
        this.detectChanges();
    }

    // sum all ecologi attr from bundle selection and not include not simple products
    updateEcologiNumber(): void {
        this.ecologiTreeNumber = 0;
        this.basket.items.forEach(item => {
            if (item.product?.isBundle) {
                this.ecologiTreeNumber =
                    this.ecologiTreeNumber +
                    sum(
                        item.product
                            .getBundleSelectedProducts(item.data.request.bundle_options)
                            ?.map(bundleItem =>
                                bundleItem.product.data?.ecologi ? bundleItem.qty * item.qty * bundleItem.product.data.ecologi : 0
                            )
                    );
            } else if (item.product?.isSimple) {
                this.ecologiTreeNumber = this.ecologiTreeNumber + (item.product.data?.ecologi ? item.qty * item.product.data.ecologi : 0);
            }
        });
    }

    protected get showSurchargeInfo(): boolean {
        return !!this.basket.items.find(item => item.product?.data?.surcharge);
    }

    /**
     * TODO: remove after upgrading df/basket > 1.0.17+
     */
    override get warning(): string | undefined {
        const beErrors = this.basket.data.errors;
        if (beErrors?.length) {
            return beErrors[0];
        }

        if (!this.isSalable) {
            return 'To proceed to checkout please remove out of stock products from your basket';
        }
    }
}
